import React, { useState } from 'react';
import { Phone, Check, AlertCircle, ArrowRight } from 'lucide-react';
import { api, TIMEPAYMENT_URL } from '../lib/api';
import { PHONE_DISPLAY, PHONE_TEL } from './CTASection';

const trackClick = (name, params = {}) => {
    if (typeof window !== 'undefined' && window.trackConversion) {
        window.trackConversion(name, params);
    }
};

/**
 * MachineInquiryCard — compact inquiry form used in the sticky right rail
 * on machine detail pages. Pre-fills the machine name and submits directly
 * to the /api/contact endpoint. Reduces friction from "click Quote → contact page → fill 8 fields".
 */
const MachineInquiryCard = ({ machineName = '', machineCategory = '' }) => {
    const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
    const [sending, setSending] = useState(false);
    const [ok, setOk] = useState(false);
    const [err, setErr] = useState('');

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setErr('');
        if (!form.phone || form.phone.trim().length < 5) {
            setErr('Phone required — email replies can end up in spam.');
            return;
        }
        setSending(true);
        try {
            await api.post('/contact', {
                name: form.name,
                email: form.email,
                phone: form.phone,
                message: form.message || `Interested in ${machineName}. Please send pricing, availability, and financing options.`,
                subject: `Equipment quote: ${machineName}`,
                machine_ref: machineName,
                preferred_service: 'Equipment Purchase',
                industry: '',
                location: '',
                company: '',
            });
            trackClick('equipment_quote_click', { location: 'machine_sidebar', machine: machineName });
            trackClick('form_submit', { form_id: 'machine_sidebar', machine: machineName });
            setOk(true);
        } catch (e2) {
            const detail = e2.response?.data?.detail;
            setErr(typeof detail === 'string' ? detail : 'Could not submit. Please call us.');
        } finally {
            setSending(false);
        }
    };

    if (ok) {
        return (
            <div className="border border-brand-primary bg-brand-primary/5 p-6" data-testid="inquiry-card-success">
                <Check className="h-8 w-8 text-brand-primary mb-3" />
                <div className="font-display font-bold uppercase text-lg text-neutral-900">
                    Message received.
                </div>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
                    A specialist will reach out shortly on the number you provided. Meanwhile, check your spam folder for email replies.
                </p>
            </div>
        );
    }

    return (
        <div className="border border-neutral-200 bg-white p-6 shadow-sm" data-testid="machine-inquiry-card">
            <div className="label-caps text-brand-primary text-xs mb-2">/ Get pricing</div>
            <div className="font-display font-bold uppercase text-lg text-neutral-900 leading-tight mb-4">
                Request Equipment Quote
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="machine-inquiry-form">
                <input
                    required
                    placeholder="Full name *"
                    value={form.name}
                    onChange={update('name')}
                    className="w-full bg-white border border-neutral-300 focus:border-brand-primary transition-colors text-neutral-900 p-3 outline-none text-sm"
                    data-testid="inquiry-name"
                />
                <input
                    required
                    type="email"
                    placeholder="Email *"
                    value={form.email}
                    onChange={update('email')}
                    className="w-full bg-white border border-neutral-300 focus:border-brand-primary transition-colors text-neutral-900 p-3 outline-none text-sm"
                    data-testid="inquiry-email"
                />
                <input
                    required
                    type="tel"
                    placeholder="Phone * (required)"
                    value={form.phone}
                    onChange={update('phone')}
                    className="w-full bg-white border border-neutral-300 focus:border-brand-primary transition-colors text-neutral-900 p-3 outline-none text-sm"
                    data-testid="inquiry-phone"
                />
                <textarea
                    rows={3}
                    placeholder={`Interested in ${machineName || 'this equipment'} — anything specific?`}
                    value={form.message}
                    onChange={update('message')}
                    className="w-full bg-white border border-neutral-300 focus:border-brand-primary transition-colors text-neutral-900 p-3 outline-none text-sm resize-none"
                    data-testid="inquiry-message"
                />
                {err && (
                    <div className="flex items-start gap-2 border border-red-300 bg-red-50 text-red-700 p-2.5 text-xs" data-testid="inquiry-error">
                        <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" /> {err}
                    </div>
                )}
                <button
                    type="submit"
                    disabled={sending}
                    className="w-full inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-white font-bold px-4 py-3 label-caps transition-colors text-sm"
                    data-testid="inquiry-submit"
                >
                    {sending ? 'Sending…' : (<>Send Quote Request <ArrowRight className="h-4 w-4" /></>)}
                </button>
            </form>

            <div className="mt-4 pt-4 border-t border-neutral-200">
                <a
                    href={`tel:${PHONE_TEL}`}
                    onClick={() => trackClick('phone_click', { location: 'machine_sidebar', machine: machineName })}
                    className="flex items-center gap-2 text-sm text-neutral-800 hover:text-brand-primary transition-colors"
                    data-testid="inquiry-call"
                >
                    <Phone className="h-4 w-4 text-brand-primary" />
                    <span className="font-bold">{PHONE_DISPLAY}</span>
                    <span className="text-neutral-500 text-xs">— Talk now</span>
                </a>
                <a
                    href={TIMEPAYMENT_URL}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackClick('finance_click', { location: 'machine_sidebar', machine: machineName })}
                    className="mt-3 flex items-center gap-2 text-sm text-neutral-800 hover:text-brand-primary transition-colors"
                    data-testid="inquiry-finance"
                >
                    <span className="inline-block h-4 w-4 text-brand-primary font-bold">$</span>
                    Finance this machine
                </a>
                <div className="mt-3 text-[11px] text-neutral-500 leading-snug">
                    Category: <span className="font-mono">{(machineCategory || '').replace(/-/g, ' ')}</span>
                </div>
            </div>
        </div>
    );
};

export default MachineInquiryCard;
